// src/pages/SubUsuarios/FormView.tsx
//
// Form em modal do módulo Sub-usuários. Dois modos:
//   • Criar  — CPF/CNPJ + as 11 permissões  → POST /usuario/sub-usuarios
//   • Editar — Switch "Ativo" + as 11 permissões → PUT /usuario/sub-usuarios/:id
// Nenhum campo de senha existe aqui (contrato do backend). Após sucesso:
// revalida a lista (mutate), fecha o modal e emite um toast.
//
// UI-2: o artboard `portal-subusuarios.dc.html` desenha no lugar deste modal um
// painel inline de 300px com Nome / Login / E-mail / Perfil — uma tela de CRIAÇÃO
// de conta, que não é o que o backend faz (ele VINCULA um usuário existente, com
// 11 permissões). O fluxo fica, mas vestido com as primitivas do kit: rótulo
// externo `.flabel` sobre campo `.fld` (§6.3), `.btnp` e ícone de traço.

import { z } from 'zod';
import { mutate } from 'swr';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';

import { api } from 'src/auditoria/infra/http/api-client';
import { TxForm } from 'src/auditoria/components/tx-form/tx-form';
import { fecharModalAtual } from 'src/auditoria/components/tx-simple-modal/tx-modal-provider';

import { Field } from 'src/components/hook-form';

import { DsField, DsButton } from 'src/auditoria/components/ds';

import {
  type SubUsuario,
  PERMISSOES_DEF,
  SUB_USUARIOS_KEY,
  permissoesVazias,
  limparCpfCnpj,
} from './types';

// ----------------------------------------------------------------------

const zPermissoes = z.object({
  emitirNfse: z.boolean(),
  corrigirNfse: z.boolean(),
  cancelarNfse: z.boolean(),
  encerrarLivros: z.boolean(),
  reabrirLivros: z.boolean(),
  emitirGuias: z.boolean(),
  cancelarGuias: z.boolean(),
  alterarContatos: z.boolean(),
  alterarSenha: z.boolean(),
  acessoCliente: z.boolean(),
  acessoContaFiscal: z.boolean(),
});

// Mesma regra do backend: CPF = 11 dígitos, CNPJ = 14. O servidor continua
// sendo a fonte de verdade; aqui só damos feedback rápido no cliente.
const zCriar = z.object({
  cpfCnpj: z.string().refine((v) => {
    const d = limparCpfCnpj(v);
    return d.length === 11 || d.length === 14;
  }, 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.'),
  permissoes: zPermissoes,
});

const zEditar = z.object({
  ativo: z.boolean(),
  permissoes: zPermissoes,
});

type CriarForm = z.infer<typeof zCriar>;
type EditarForm = z.infer<typeof zEditar>;

// ----------------------------------------------------------------------

/** `.ferr` (§6.3): 11px/600 sem o recuo lateral do `FormHelperText`. */
const SX_CAMPO = {
  '& .MuiFormHelperText-root': {
    fontSize: 11,
    fontWeight: 600,
    margin: '6px 0 0',
  },
};

// Grade dos 11 checkboxes de permissão — compartilhada por criar/editar.
// Nomes aninhados (permissoes.<chave>) para o RHF montar o objeto sozinho.
function PermissoesGrid() {
  return (
    <Box
      sx={{
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ fontSize: 13, fontWeight: 800, mb: '8px' }}>Permissões</Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: '16px',
        }}
      >
        {PERMISSOES_DEF.map(({ chave, label }) => (
          <Field.Checkbox key={chave} name={`permissoes.${chave}`} label={label} />
        ))}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

// Extrai o HTTP status de um erro do axios sem depender do tipo do axios.
function getHttpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

// Busca do usuário a vincular: ao clicar em Buscar, consulta
// GET /usuario/sub-usuarios/buscar-usuario e mostra o NOME do titular do CPF/CNPJ
// (ou 404 "não encontrado"), para o operador confirmar quem está vinculando antes
// do submit. Usa o form context do TxForm.
function BuscaUsuario() {
  const { watch } = useFormContext<CriarForm>();
  const cpfCnpj = watch('cpfCnpj');

  const [nome, setNome] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);

  // Zera o resultado sempre que o CPF/CNPJ muda (evita nome "grudado" de outra busca).
  useEffect(() => {
    setNome(null);
    setErro(null);
  }, [cpfCnpj]);

  const buscar = async () => {
    const d = limparCpfCnpj(cpfCnpj || '');
    if (d.length !== 11 && d.length !== 14) {
      setNome(null);
      setErro('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) para buscar.');
      return;
    }
    try {
      setBuscando(true);
      setErro(null);
      const resp = await api.get<{
        usuario: { usrCodigo: number; nome: string; cpfCnpj: string };
      }>('/portal-api/usuario/sub-usuarios/buscar-usuario', {
        params: { cpfCnpj: d },
      });
      setNome(resp.usuario.nome || '(usuário sem nome cadastrado)');
    } catch (e) {
      setNome(null);
      setErro(
        getHttpStatus(e) === 404
          ? 'Nenhum usuário cadastrado com este CPF/CNPJ. O titular precisa já ter conta no portal.'
          : 'Não foi possível buscar o usuário. Tente novamente.'
      );
    } finally {
      setBuscando(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
        <DsField label="CPF / CNPJ" density="sm" sx={[SX_CAMPO, { flex: 1, minWidth: 0 }]}>
          <Field.Text name="cpfCnpj" placeholder="000.000.000-00" autoFocus />
        </DsField>

        <DsButton tone="neutral" icon="buscar" onClick={buscar} disabled={buscando}>
          Buscar
        </DsButton>
      </Box>

      {nome && (
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: 'success.dark',
            mt: '8px',
          }}
        >
          Usuário encontrado: {nome}
        </Box>
      )}
      {erro && (
        <Box sx={{ fontSize: 11, fontWeight: 600, color: 'error.main', mt: '8px' }}>{erro}</Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

interface FormViewProps {
  subUsuario?: SubUsuario;
}

export default function FormView({ subUsuario }: FormViewProps) {
  const isEdit = Boolean(subUsuario);

  if (!isEdit) {
    const onSubmitCriar = async (data: CriarForm) => {
      await api.post(SUB_USUARIOS_KEY, {
        cpfCnpj: limparCpfCnpj(data.cpfCnpj),
        permissoes: data.permissoes,
      });
      await mutate(SUB_USUARIOS_KEY);
      fecharModalAtual();
      toast.success('Sub-usuário vinculado com sucesso.');
    };

    return (
      <TxForm<CriarForm>
        schema={zCriar}
        defaultValues={{ cpfCnpj: '', permissoes: permissoesVazias() }}
        onSubmit={onSubmitCriar}
        sx={{ p: 1 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            O usuário precisa já ter cadastro no sistema. Informe o CPF/CNPJ dele para vinculá-lo à
            sua empresa — nenhuma senha é criada ou alterada aqui.
          </Box>

          <BuscaUsuario />

          <PermissoesGrid />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <DsButton type="submit">Vincular</DsButton>
          </Box>
        </Box>
      </TxForm>
    );
  }

  const alvo = subUsuario!;

  const onSubmitEditar = async (data: EditarForm) => {
    await api.put(`${SUB_USUARIOS_KEY}/${alvo.id}`, {
      ativo: data.ativo,
      permissoes: data.permissoes,
    });
    await mutate(SUB_USUARIOS_KEY);
    fecharModalAtual();
    toast.success('Sub-usuário atualizado com sucesso.');
  };

  return (
    <TxForm<EditarForm>
      schema={zEditar}
      defaultValues={{ ativo: alvo.ativo, permissoes: { ...alvo.permissoes } }}
      onSubmit={onSubmitEditar}
      sx={{ p: 1 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Field.Switch name="ativo" label="Ativo" />

        <PermissoesGrid />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <DsButton type="submit">Salvar</DsButton>
        </Box>
      </Box>
    </TxForm>
  );
}
